<template>
  <div
    v-if="algoliaClient"
    class="predictive-search-wrapper"
  >
    <div class="predictive-search">
      <div class="predictive-search__inner">
        <div
          v-if="!isMobile"
          class="predictive-search__header"
        >
          <button
            class="predictive-search__close icon-button"
            type="button"
            @click.prevent="handleClose"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.close')"
            />

            <icon-close />
          </button>
        </div>

        <div
          class="predictive-search__scroll"
          js-overlay="scrollElement"
        >
          <div
            v-if="searchQuery && !noResults"
            class="predictive-search__body"
          >
            <predictive-search-results
              :articles="secondaryResults.articles"
              :collections="secondaryResults.collections"
              :pages="secondaryResults.pages"
              :products="productResults"
              :suggestions="secondaryResults.suggestions"
              :search-query="searchQuery"
            />
          </div>

          <div
            v-else
            class="predictive-search__empty"
          >
            <predictive-search-empty
              :search-query="searchQuery"
            />
          </div>
        </div>

        <transition name="fade">
          <div
            v-if="searchQuery && hasResults"
            class="predictive-search__footer"
          >
            <btn
              class="predictive-search__view-all"
              modifiers="primary block center"
              :label="
                $string(
                  'predictive_search.view_all',
                  { replace: { term: searchQuery } },
                )
              "
              :url="viewAllUrl"
              @click.prevent="handleViewAll"
            />
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Predictive search (predictive-search)
 * -----------------------------------------------------------------------------
 * Predictive search overlay powered by Algolia.
 * - All indices (products, collections, pages, suggestions) fetched in a single
 *   multi-index batch request for optimal performance (1 query per keystroke).
 *
 * @param {Object} overlay - Overlay API.
 * @param {String} searchQuery - Current search query.
 *
 */
import { mapState } from 'vuex'

import Btn from '~global/btn/btn'
import PredictiveSearchEmpty from '~async/predictive-search/predictive-search-empty'
import PredictiveSearchResults from '~async/predictive-search/predictive-search-results'

import IconClose from '~icons/directional-navigation/close.svg'

import { formatAlgoliaProduct } from '~/format/format-algolia'
import { getAlgoliaConfig, getAlgoliaRedirectUrl, getRawSearchClient, handleAlgoliaRedirect } from '~/helpers/algolia-client'

export default {
  name: 'PredictiveSearch',

  components: {
    Btn,
    IconClose,
    PredictiveSearchEmpty,
    PredictiveSearchResults,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },

  data() {
    const algoliaConfig = getAlgoliaConfig()

    return {
      algoliaIndexProducts: algoliaConfig.mainIndex,
      algoliaClient: getRawSearchClient(),
      secondaryIndices: {
        collections: algoliaConfig.indices?.collections || 'shopify_collections',
        pages: algoliaConfig.indices?.pages || 'shopify_pages',
        articles: algoliaConfig.indices?.articles || 'shopify_articles',
        suggestions: algoliaConfig.indices?.suggestions || 'shopify_products_query_suggestions',
      },
      disabledIndices: new Set(),
      hasResults: false,
      debounceTimeout: null,
      productResults: [],
      secondaryResults: {
        collections: [],
        pages: [],
        articles: [],
        suggestions: [],
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Check if there are no results across all indices.
     * @returns {Boolean}
     */
    noResults() {
      return !this.productResults.length &&
        !this.secondaryResults.collections.length &&
        !this.secondaryResults.pages.length &&
        !this.secondaryResults.articles.length &&
        !this.secondaryResults.suggestions.length
    },

    /**
     * URL for "View all" button.
     * @returns {String}
     */
    viewAllUrl() {
      return `${this.$variable('routes.search')}?q=${this.searchQuery}&type=product`
    },
  },

  watch: {

    /**
     * Watch for changes in search query.
     * - Debounces fetch to reduce API calls during fast typing.
     */
    searchQuery(query) {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }

      if (!query) {
        this.productResults = []
        this.secondaryResults = { collections: [], pages: [], articles: [], suggestions: [] }
        this.hasResults = false
        window.algoliaRedirectUrl = null
        return
      }

      this.debounceTimeout = setTimeout(() => {
        this.fetchAllResults(query)
      }, 300)
    },
  },

  async mounted() {
    await this.$nextTick()
    if (this.searchQuery) {
      this.fetchAllResults(this.searchQuery)
    }
  },

  methods: {

    /**
     * Fetch results from all Algolia indices in a single batch request.
     * Combines products + secondary indices into one API call.
     * @param {String} query - Search query.
     */
    async fetchAllResults(query) {
      if (!query || !this.algoliaClient) {
        this.productResults = []
        this.secondaryResults = { collections: [], pages: [], articles: [], suggestions: [] }
        this.hasResults = false
        return
      }

      const requests = [
        {
          indexName: this.algoliaIndexProducts,
          query,
          hitsPerPage: 6,
          clickAnalytics: true,
          distinct: true,
          attributesToHighlight: ['title'],
          highlightPreTag: '<strong class="product-card__title-highlight">',
          highlightPostTag: '</strong>',
        },
      ]

      const indexKeys = ['collections', 'pages', 'articles', 'suggestions']
      const activeKeys = indexKeys.filter(
        (key) => !this.disabledIndices.has(this.secondaryIndices[key]),
      )

      activeKeys.forEach((key) => {
        requests.push({
          indexName: this.secondaryIndices[key],
          query,
          hitsPerPage: 3,
        })
      })

      let results
      try {
        const response = await this.algoliaClient.search(requests)
        results = response.results || []
      } catch {
        this.productResults = []
        this.secondaryResults = { collections: [], pages: [], articles: [], suggestions: [] }
        this.hasResults = false
        return
      }

      /**
       * Store redirect URL for search-bar to use on form submit.
       * No redirect during typing - only on Enter submission.
       */
      window.algoliaRedirectUrl = getAlgoliaRedirectUrl(results[0])

      // First result is always products
      const productHits = results[0]?.hits || []
      this.productResults = productHits.slice(0, 6).map((hit) => formatAlgoliaProduct(hit))

      // Map remaining results to secondary index keys
      const resultMap = {}
      let resultIndex = 1
      for (const key of indexKeys) {
        if (this.disabledIndices.has(this.secondaryIndices[key])) {
          resultMap[key] = []
          continue
        }
        const result = results[resultIndex]
        resultMap[key] = result?.hits || []
        if (result?.status === 404 || result?.status === 400) {
          this.disabledIndices.add(this.secondaryIndices[key])
          resultMap[key] = []
        }
        resultIndex++
      }

      const { collections, pages, articles, suggestions } = resultMap

      this.secondaryResults = {
        collections: collections.map((hit) => ({
          handle: hit.handle,
          title: hit.title,
          url: `${cnvs.routes.normalisedRoot}collections/${hit.handle}`,
        })),
        pages: pages.map((hit) => ({
          handle: hit.handle,
          title: hit.title,
          onlineStoreUrl: `${cnvs.routes.normalisedRoot}pages/${hit.handle}`,
        })),
        articles: articles.map((hit) => ({
          handle: hit.handle,
          title: hit.title,
          onlineStoreUrl: hit.url ||
            `${cnvs.routes.normalisedRoot}blogs/${hit.blog_handle || 'news'}/${hit.handle}`,
        })),
        suggestions: suggestions.map((hit) => ({
          text: hit.query || hit.title || '',
        })),
      }

      this.hasResults = !this.noResults

      await this.$nextTick()
      if (this.overlay.refresh) {
        const activeElement = document.activeElement
        const isTypingInSearchInput = activeElement?.classList?.contains('search-bar__input')

        // Skip refresh on mobile when typing to prevent focus loss
        if (this.isMobile && isTypingInSearchInput) {
          return
        }

        // Use activeElement if it's the search input (handles mobile correctly)
        const searchInput = isTypingInSearchInput
          ? activeElement
          : document.querySelector('.search-bar__input')
        this.overlay.refresh({ currentElement: searchInput || activeElement })
      }
    },

    /**
     * Handle "View all" button click.
     * - Checks for Algolia redirect URL first to prevent flash of content.
     * - Falls back to normal search page if no redirect.
     */
    handleViewAll() {
      if (handleAlgoliaRedirect()) {
        return
      }

      window.location.href = this.viewAllUrl
    },

    /**
     * Close predictive search overlay.
     */
    handleClose() {
      this.overlay.close()
    },
  },
}
</script>

<style lang="scss">
@import './predictive-search';
@import './predictive-search-empty';
@import './predictive-search-results';
</style>
